import { useState, useEffect, useCallback, useRef } from "react";
import {
  getChannels, getChannel,
  logActivity, saveChannel,
  getVideos, saveVideos,
  getTracking, bulkUpdateTracking,
  getClips, updateClips,
  getTranscript, updateTranscript,
  saveAiSuggestions, getAiSuggestions,
  savePlaylistMap, getPlaylistMap, savePlaylists,
  getClassificationSchema, saveClassificationSchema, DEFAULT_TYPES, suggestTypesFromPlaylists,
  getBranding, saveBranding,
  getRefreshLog, saveRefreshLog,
  appendQuotaEntry,
  saveVideoType, getVideoTypes,
} from "../firebase";
import { classifyVideo, validateApiKey, validateChannelInput } from "../classifier";
import {
  ytProxy, normalizeVideo,
} from "../youtube";
import { WriteDebouncer, quotaGuard, cache } from "../cacheLayer";

const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours
const ptDateKey = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });

export function useChannel(user, userRole) {
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [videos, setVideos] = useState([]);
  const [tracking, setTracking] = useState({});
  const [clips, setClips] = useState({});
  const [transcripts, setTranscripts] = useState({}); // lazy-loaded per video
  const [branding, setBranding] = useState("");
  const [refreshLog, setRefreshLog] = useState({});
  const [videoTypes, setVideoTypes] = useState({});
  const [aiSuggestions, setAiSuggestions] = useState({});
  const [playlistMap, setPlaylistMap] = useState({});
  const [classificationSchema, setClassificationSchema] = useState(DEFAULT_TYPES);
  const [schemaSuggestions, setSchemaSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadPhase, setLoadPhase] = useState("");
  const [progress, setProgress] = useState({ n: 0, total: 0 });
  const [error, setError] = useState(null);
  const [quotaBlocked, setQuotaBlocked] = useState(false);

  const refreshTimer = useRef(null);
  const trackingDebouncer = useRef(null);
  const isVisible = useRef(true);
  const lastActivity = useRef(Date.now());

  // Track tab visibility — don't refresh when tab is hidden
  useEffect(() => {
    const onVisible = () => { isVisible.current = !document.hidden; };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Track user activity
  useEffect(() => {
    const onActivity = () => { lastActivity.current = Date.now(); };
    window.addEventListener("mousemove", onActivity);
    window.addEventListener("keydown", onActivity);
    return () => {
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("keydown", onActivity);
    };
  }, []);

  // Init tracking debouncer
  useEffect(() => {
    if (!activeId) return;
    trackingDebouncer.current = new WriteDebouncer(async (batch) => {
      // batch = { videoId: trackingData }
      const current = cache.get("tracking", activeId) || {};
      const merged = { ...current };
      Object.entries(batch).forEach(([vid, data]) => { merged[vid] = data; });
      await bulkUpdateTracking(activeId, merged);
    }, 1500);
    return () => trackingDebouncer.current?.destroy();
  }, [activeId]);

  // Load channels when user is authenticated
  useEffect(() => {
    if (!user || !userRole) return;
    const isAdmin = userRole.role === "admin" ||
      ["jcosta89@gmail.com","cole.the.slaw@gmail.com"].includes(user.email);

    if (isAdmin) {
      // Admins can read all channels
      getChannels().then(chs => {
        setChannels(chs);
        if (chs.length) setActiveId(chs[0].id);
      }).catch(e => {
        if (e.message?.includes("quota")) setQuotaBlocked(true);
      });
    } else {
      // Non-admins fetch only their assigned channels individually
      const ids = userRole.channelIds || [];
      if (!ids.length) { setChannels([]); return; }
      Promise.all(ids.map(id => getChannel(id)))
        .then(chs => {
          const valid = chs.filter(Boolean);
          // Free tier: limit to 1 channel
          const status = userRole.subscription_status;
          const isActive = status === "active" || status === "trialing";
          const limited = (!isActive && userRole.plan !== "agency" && userRole.plan !== "pro" && userRole.plan !== "solo")
            ? valid.slice(0, 1)
            : valid;
          setChannels(limited);
          if (limited.length) setActiveId(limited[0].id);
        })
        .catch(e => console.error("[CC] Channel load error:", e.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, userRole?.role]);

  // Load channel data when active channel changes
  // Parallel fetch for maximum speed, cache-first for all
  useEffect(() => {
    if (!activeId) return;

    if (quotaGuard.isBlocked()) {
      setQuotaBlocked(true);
      setError("Firestore quota reached. Reads paused — using cached data.");
      return;
    }

    Promise.all([
      getVideos(activeId),
      getTracking(activeId),
      getClips(activeId),
      getRefreshLog(activeId),
      getBranding(activeId),
      getVideoTypes(activeId),
      getAiSuggestions(activeId),
      getPlaylistMap(activeId),
      getClassificationSchema(activeId),
    ]).then(([v, t, c, rl, b, vt, aisugs, pm, schema]) => {
      setVideos(v);
      setTracking(t);
      setClips(c);
      setRefreshLog(rl);
      setBranding(b);
      setVideoTypes(vt);
      setAiSuggestions(aisugs);
      setPlaylistMap(pm);
      setClassificationSchema(schema || DEFAULT_TYPES);
      setQuotaBlocked(false);
    }).catch(e => {
      if (e.message?.includes("quota") || e.message?.includes("Quota")) {
        setQuotaBlocked(true);
        setError(e.message);
      } else {
        setError(e.message);
      }
    });
  }, [activeId]);

  // Log YouTube API quota
  const logQuota = useCallback(async (op, units) => {
    const entry = { op, units, ts: new Date().toISOString() };
    await appendQuotaEntry(ptDateKey(), entry).catch(() => {});
  }, []);

  // Incremental refresh — only new videos + refresh last 20 stats
  // Token getter — passed to ytProxy so it can auth each YouTube API call
  const getToken = useCallback(() => user?.getIdToken(false), [user]);

  const incrementalRefresh = useCallback(async (channel) => {
    if (!channel?.apiKey) return;
    if (quotaGuard.isBlocked()) return;
    // Skip if tab is hidden or user has been inactive for 30+ minutes
    if (!isVisible.current) return;
    if (Date.now() - lastActivity.current > 30 * 60 * 1000) return;

    try {
      const info = await ytProxy('channelInfo', channel.id, { input: channel.ytId || channel.id }, getToken);
      await logQuota("channels.list", 1);
      const uploadsId = info.contentDetails.relatedPlaylists.uploads;
      // Use most recent sync date to avoid scanning entire playlist
      const since = refreshLog.lastAutoRefresh || refreshLog.lastFullRefresh || 
        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(); // fallback: 7 days ago

      const newIds = await ytProxy('newVideosSince', channel.id, { playlistId: uploadsId, sinceDate: since }, getToken);
      if (newIds.length > 0) {
        await logQuota("playlistItems.list", Math.ceil(newIds.length / 50));
        const details = await ytProxy('videoDetails', channel.id, { ids: newIds }, getToken);
        await logQuota("videos.list", Math.ceil(newIds.length / 50));
        const normalized = details.map(normalizeVideo);
        await saveVideos(channel.id, normalized);
        setVideos(prev => {
          const existing = new Set(prev.map(v => v.id));
          const brandNew = normalized.filter(v => !existing.has(v.id));
          return [...brandNew, ...prev].sort((a, b) =>
            new Date(b.publishedAt) - new Date(a.publishedAt)
          );
        });
      }

      // Refresh stats on last 20 only
      const recentIds = videos.slice(0, 20).map(v => v.id);
      if (recentIds.length > 0) {
        const updated = await ytProxy('videoDetails', channel.id, { ids: recentIds }, getToken);
        await logQuota("videos.list", Math.ceil(recentIds.length / 50));
        const normalized = updated.map(normalizeVideo);
        await saveVideos(channel.id, normalized);
        setVideos(prev => prev.map(v => normalized.find(u => u.id === v.id) || v));
      }

      const rl = { lastAutoRefresh: new Date().toISOString() };
      await saveRefreshLog(channel.id, { ...refreshLog, ...rl });
      setRefreshLog(prev => ({ ...prev, ...rl }));
    } catch {
      // Silent fail — auto-refresh errors don't surface to the user
    }
  }, [refreshLog, videos, logQuota, getToken]);

  // Smart auto-refresh scheduler
  useEffect(() => {
    if (!activeId) return;
    const channel = channels.find(c => c.id === activeId);
    if (!channel?.apiKey) return;

    const schedule = () => {
      const last = refreshLog.lastAutoRefresh;
      const next = last ? new Date(last).getTime() + REFRESH_INTERVAL_MS : Date.now() + REFRESH_INTERVAL_MS;
      const delay = Math.max(5 * 60 * 1000, next - Date.now()); // minimum 5 min delay
      refreshTimer.current = setTimeout(async () => {
        if (!quotaGuard.isBlocked() && isVisible.current) {
          await incrementalRefresh(channel);
        }
        schedule();
      }, delay);
    };

    schedule();
    return () => clearTimeout(refreshTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, refreshLog.lastAutoRefresh]);

  const manualRefresh = useCallback(async () => {
    const channel = channels.find(c => c.id === activeId);
    if (!channel?.apiKey) return;
    setLoading(true);
    setError(null);
    try {
      // Direct fetch — bypass inactivity/visibility checks
      const info = await ytProxy('channelInfo', channel.id, { input: channel.ytId || channel.id }, getToken);
      await logQuota("channels.list", 1);
      const uploadsId = info.contentDetails.relatedPlaylists.uploads;

      // Look back 30 days to catch anything missed
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const newIds = await ytProxy('newVideosSince', channel.id, { playlistId: uploadsId, sinceDate: since }, getToken);

      if (newIds.length > 0) {
        await logQuota("playlistItems.list", Math.ceil(newIds.length / 50));
        const details = await ytProxy('videoDetails', channel.id, { ids: newIds }, getToken);
        await logQuota("videos.list", Math.ceil(newIds.length / 50));
        const normalized = details.map(normalizeVideo);
        await saveVideos(channel.id, normalized);
        setVideos(prev => {
          const existing = new Set(prev.map(v => v.id));
          const brandNew = normalized.filter(v => !existing.has(v.id));
          if (brandNew.length > 0) {
            return [...brandNew, ...prev].sort((a, b) =>
              new Date(b.publishedAt) - new Date(a.publishedAt)
            );
          }
          return prev;
        });
      }

      // Always refresh stats on last 20
      const recentIds = videos.slice(0, 20).map(v => v.id);
      if (recentIds.length > 0) {
        const updated = await ytProxy('videoDetails', channel.id, { ids: recentIds }, getToken);
        await logQuota("videos.list", 1);
        const normalized = updated.map(normalizeVideo);
        await saveVideos(channel.id, normalized);
        setVideos(prev => prev.map(v => normalized.find(u => u.id === v.id) || v));
      }

      const rl = { lastAutoRefresh: new Date().toISOString() };
      await saveRefreshLog(channel.id, { ...refreshLog, ...rl });
      setRefreshLog(prev => ({ ...prev, ...rl }));
      await logActivity(channel.id, { type: "channel_refresh", detail: `${channel.name} synced`, userEmail: user?.email, userName: user?.displayName || user?.email?.split("@")[0], userId: user?.uid });
    } catch(e) {
      setError("Refresh failed: " + e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, activeId, videos, refreshLog, logQuota, getToken]);

  // Full fetch after confirm dialog
  const executeFetch = useCallback(async (channel, uploadsId, limit) => {
    setLoading(true); setLoadPhase("videos"); setProgress({ n: 0, total: 0 });
    try {
      const ids = await ytProxy('videoIds', channel.id, { playlistId: uploadsId, limit }, getToken);
      await logQuota("playlistItems.list", Math.ceil(ids.length / 50));

      const details = await ytProxy('videoDetails', channel.id, { ids }, getToken);
      await logQuota("videos.list", Math.ceil(ids.length / 50));

      const normalized = details
        .map(normalizeVideo)
        .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

      await saveVideos(channel.id, normalized);
      setVideos(normalized);
      cache.invalidate("videos", channel.id);

      // Fetch playlists and build video→playlist map
      try {
        setLoadPhase("playlists");
        const playlists = await ytProxy('channelPlaylists', channel.id, { channelId: channel.id }, getToken);
        await savePlaylists(channel.id, playlists);
        await logQuota("playlists.list", Math.ceil(playlists.length / 50) || 1);

        if (playlists.length > 0 && playlists.length < 100) {
          setLoadPhase("playlist-map");
          const pm = await ytProxy('buildPlaylistMap', channel.id, { playlists }, getToken);
          await savePlaylistMap(channel.id, pm);
          setPlaylistMap(pm);
          const pmCalls = playlists.filter(p => p.videoCount > 0 && p.videoCount < 2000).length;
          await logQuota("playlistItems.list", pmCalls);
        }
      } catch(e) {
        // Playlist fetch is non-critical — don't fail the whole pull
        console.warn("[CC] Playlist fetch failed:", e.message);
      }

      const rl = {
        lastFullRefresh: new Date().toISOString(),
        lastAutoRefresh: new Date().toISOString(),
        totalFetched: normalized.length,
      };
      await saveRefreshLog(channel.id, rl);
      setRefreshLog(rl);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false); setLoadPhase("");
    }
  }, [logQuota, getToken]);

  // Add channel
  // Mark a clip as posted to a platform with a URL
  const updateClipPosted = useCallback(async (videoId, clipId, platform, url) => {
    const existing = clips[videoId] || [];
    const updated = existing.map(c => {
      if (c.id !== clipId) return c;
      const posted = { ...(c.posted || {}) };
      if (url) {
        posted[platform] = { url, postedAt: new Date().toISOString() };
      } else {
        delete posted[platform];
      }
      return { ...c, posted };
    });
    await updateClips(activeId, videoId, updated);
    setClips(prev => ({ ...prev, [videoId]: updated }));
  }, [clips, activeId]);


  const addChannel = useCallback(async (name, ytId, apiKey, channelType = "main") => {
    const keyCheck = validateApiKey(apiKey);
    if (!keyCheck.valid) { setError(keyCheck.error); return null; }
    const idCheck = validateChannelInput(ytId);
    if (!idCheck.valid) { setError(idCheck.error); return null; }

    setLoading(true); setLoadPhase("channel-info"); setError(null);
    try {
      // Channel not yet in Firestore — use validateChannel proxy action which accepts apiKey directly
      const info = await ytProxy('validateChannel', null, { apiKey, input: ytId }, getToken);
      await logQuota("channels.list", 1);
      const id = info.id;
      const channel = {
        id, name, ytId: idCheck.value, apiKey, channelType,
        title: info.snippet.title,
        description: info.snippet.description,
        thumbnail: info.snippet.thumbnails?.default?.url,
        subscriberCount: parseInt(info.statistics?.subscriberCount || 0),
        videoCount: parseInt(info.statistics?.videoCount || 0),
        viewCount: parseInt(info.statistics?.viewCount || 0),
        uploadsPlaylistId: info.contentDetails.relatedPlaylists.uploads,
        createdAt: new Date().toISOString(),
      };
      await saveChannel(channel);
      setChannels(prev => [...prev, channel]);
      setActiveId(id);
      setLoading(false); setLoadPhase("");
      return { channel, uploadsId: channel.uploadsPlaylistId, totalVideos: channel.videoCount };
    } catch (e) {
      setError(e.message);
      setLoading(false); setLoadPhase("");
      return null;
    }
  }, [logQuota, getToken]);

  // Tracking — debounced writes
  const toggleTracking = useCallback(async (videoId, field) => {
    const curr = tracking[videoId] || {};
    const updated = { ...curr, [field]: !curr[field] };
    setTracking(prev => ({ ...prev, [videoId]: updated }));
    // Debounced — waits 1.5s before writing
    trackingDebouncer.current?.set(videoId, updated);
  }, [tracking]);

  const bulkApplyTracking = useCallback(async (videoIds, field) => {
    const updates = {};
    videoIds.forEach(id => {
      updates[id] = { ...(tracking[id] || {}), [field]: true };
    });
    setTracking(prev => ({ ...prev, ...updates }));
    await bulkUpdateTracking(activeId, { ...(cache.get("tracking", activeId) || {}), ...updates });
  }, [tracking, activeId]);

  // Clips
  const saveClipsForVideo = useCallback(async (videoId, newClips) => {
    setClips(prev => ({ ...prev, [videoId]: newClips }));
    await updateClips(activeId, videoId, newClips);
  }, [activeId]);

  // Transcripts — lazy loaded per video
  const loadTranscript = useCallback(async (videoId) => {
    if (transcripts[videoId] !== undefined) return transcripts[videoId];
    const text = await getTranscript(activeId, videoId);
    setTranscripts(prev => ({ ...prev, [videoId]: text }));
    return text;
  }, [activeId, transcripts]);

  const saveTranscript = useCallback(async (videoId, text) => {
    setTranscripts(prev => ({ ...prev, [videoId]: text }));
    await updateTranscript(activeId, videoId, text);
    const curr = tracking[videoId] || {};
    if (!curr.transcript) {
      const updated = { ...curr, transcript: true };
      setTracking(prev => ({ ...prev, [videoId]: updated }));
      trackingDebouncer.current?.set(videoId, updated);
    }
  }, [activeId, tracking]);

  // Branding
  const saveBrandingNotes = useCallback(async (text) => {
    setBranding(text);
    await saveBranding(activeId, text);
  }, [activeId]);

  // Type overrides
  const overrideVideoType = useCallback(async (videoId, type) => {
    if (type === null) {
      // Reset to auto
      const updated = { ...videoTypes };
      delete updated[videoId];
      setVideoTypes(updated);
      await saveVideoType(activeId, videoId, null);
      return;
    }
    const data = { type, manual: true, confidence: 100, signals: ["manual override"] };
    setVideoTypes(prev => ({ ...prev, [videoId]: data }));
    await saveVideoType(activeId, videoId, data);
  }, [activeId, videoTypes]);

  // Save AI classification result — persists but NOT marked as manual (user can still override)
  const saveAiClassification = useCallback(async (videoId, result) => {
    const data = {
      type: result.type,
      manual: false,
      aiClassified: true,
      confidence: result.confidence || 80,
      signals: result.signals || ["AI classification"],
      reason: result.reason || "",
    };
    setVideoTypes(prev => ({ ...prev, [videoId]: data }));
    await saveVideoType(activeId, videoId, data);
  }, [activeId]);

  const getVideoType = useCallback((video, transcript = "") => {
    const override = videoTypes[video.id];
    if (override?.manual) return override;
    const membership = playlistMap[video.id] || [];
    return classifyVideo(video, transcript, membership, classificationSchema);
  }, [videoTypes, playlistMap, classificationSchema]);

  const updateClassificationSchema = useCallback(async (types) => {
    setClassificationSchema(types);
    await saveClassificationSchema(activeId, types);
  }, [activeId]);

  // AI Suggestions persistence
  const saveAiSuggestionsForVideo = useCallback(async (videoId, suggestions) => {
    setAiSuggestions(prev => ({ ...prev, [videoId]: { suggestions, savedAt: new Date().toISOString() } }));
    try {
      await saveAiSuggestions(activeId, videoId, suggestions);
    } catch (err) {
      // Non-fatal: suggestions saved in local state, Firestore write may fail for non-owners
      console.warn("Could not persist AI suggestions:", err.message);
    }
  }, [activeId]);


  const resyncPlaylists = useCallback(async () => {
    const channel = channels.find(c => c.id === activeId);
    if (!channel?.apiKey) return;
    try {
      const playlists = await ytProxy('channelPlaylists', channel.id, { channelId: channel.id }, getToken);
      await savePlaylists(channel.id, playlists);
      await logQuota("playlists.list", Math.ceil(playlists.length / 50) || 1);
      if (playlists.length > 0 && playlists.length < 100) {
        const pm = await ytProxy('buildPlaylistMap', channel.id, { playlists }, getToken);
        await savePlaylistMap(channel.id, pm);
        setPlaylistMap(pm);
        // Suggest new types based on playlist names
        const suggestions = suggestTypesFromPlaylists(playlists, classificationSchema);
        setSchemaSuggestions(suggestions);
        const pmCalls = playlists.filter(p => p.videoCount > 0 && p.videoCount < 2000).length;
        await logQuota("playlistItems.list", pmCalls);
      }
      return true;
    } catch(e) {
      setError("Playlist sync failed: " + e.message);
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, activeId, logQuota, getToken]);

  // Search
  const searchVideos = useCallback((q, searchFields = ["title", "description", "tags"]) => {
    if (!q.trim()) return videos;
    const lower = q.toLowerCase();
    return videos.filter(v => {
      if (searchFields.includes("title") && v.title?.toLowerCase().includes(lower)) return true;
      if (searchFields.includes("description") && v.description?.toLowerCase().includes(lower)) return true;
      if (searchFields.includes("tags") && v.tags?.some(t => t.toLowerCase().includes(lower))) return true;
      if (searchFields.includes("transcript") && transcripts[v.id]?.toLowerCase().includes(lower)) return true;
      if (searchFields.includes("clips")) {
        const vc = clips[v.id] || [];
        if (vc.some(c => c.title?.toLowerCase().includes(lower) || c.notes?.toLowerCase().includes(lower))) return true;
      }
      return false;
    });
  }, [videos, transcripts, clips]);

  return {
    channels, activeId, setActiveId, updateClipPosted,
    videos, tracking, clips, transcripts, branding, refreshLog,
    loading, loadPhase, progress, error, setError, quotaBlocked,
    videoTypes, getVideoType, overrideVideoType, saveAiClassification,
    toggleTracking, bulkApplyTracking,
    saveClipsForVideo, loadTranscript, saveTranscript, saveBrandingNotes,
    addChannel, executeFetch, manualRefresh,
    searchVideos, logQuota,
    aiSuggestions, saveAiSuggestionsForVideo,
    playlistMap, resyncPlaylists,
    classificationSchema, updateClassificationSchema, schemaSuggestions, setSchemaSuggestions,
    userRole,

    // eslint-disable-next-line no-unused-vars
    logActivity: (type, detail) => user ? logActivity(activeId, { type, detail, userEmail: user.email, userName: user.displayName || user.email?.split("@")[0], userId: user.uid }) : Promise.resolve(),
  };
}
